import React from 'react';
import './App.css';

function App() {
  return (
    <div className="App">
      <div className="container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: April 29, 2026</p>

        <div className="section">
          <p>At Uro Crash, we respect user privacy and are committed to protecting their personal data. This privacy policy explains how we collect, use, and protect information when using the application.</p>
        </div>

        <div className="section">
          <h2>1. Information We Collect</h2>
          <p>We may collect the following types of information:</p>
          
          <h3>a. Personal Information:</h3>
          <ul>
            <li>Email address</li>
            <li>Phone number (if you choose to provide it for account recovery, verification, or support)</li>
            <li>Account type (Student – Teacher)</li>
            <li>Name and contact information</li>
          </ul>

          <h3>b. Demographic Information (if you choose to provide it):</h3>
          <ul>
            <li>Demographics such as age range, gender, race/ethnicity, religion, or similar profile information</li>
          </ul>

          <h3>c. Educational Information:</h3>
          <ul>
            <li>Registered courses and lectures</li>
            <li>Test results and grades</li>
            <li>Academic performance statistics</li>
            <li>Attendance and absence records</li>
            <li>Learning progress and achievements</li>
          </ul>

          <h3>d. Technical Information:</h3>
          <ul>
            <li>Device type and operating system</li>
            <li>IP address</li>
            <li>App usage data (for performance improvement)</li>
            <li>Browser type and version</li>
            <li>Log data and analytics</li>
          </ul>
        </div>

        <div className="section">
          <h2>2. How We Use Information</h2>
          <p>We use the data we collect for:</p>
          <ul>
            <li>Operating and improving app services</li>
            <li>Enabling communication between teachers and students</li>
            <li>Displaying statistics and educational reports</li>
            <li>Managing tests and attendance</li>
            <li>Sending important notifications and alerts</li>
            <li>Improving user experience and technical support</li>
            <li>Providing personalized learning recommendations</li>
            <li>Ensuring platform security and preventing fraud</li>
          </ul>
        </div>

        <div className="section">
          <h2>3. Information Sharing</h2>
          <div className="highlight">
            <p>We do not sell personal data. We may share personal data only in the following cases:</p>
          </div>
          <ul>
            <li>When we have user consent</li>
            <li>If required by law or legal proceedings</li>
            <li>To protect the rights and security of users or the application</li>
            <li>With service providers who assist in operating our platform (for example: hosting, database, email/communications, customer support, analytics/performance monitoring) under contractual confidentiality and security obligations</li>
          </ul>
          <p>Where service providers process personal data on our behalf, they are permitted to use it only to provide services to us and not for their own purposes.</p>
        </div>

        <div className="section">
          <h2>4. Data Protection</h2>
          <p>We use appropriate security and technical measures to protect data from:</p>
          <ul>
            <li>Unauthorized access</li>
            <li>Modification, disclosure, or destruction</li>
            <li>Data breaches and cyber attacks</li>
            <li>Loss or accidental deletion</li>
          </ul>
          <div className="highlight">
            <p><strong>Security Measures:</strong></p>
            <ul>
              <li>End-to-end encryption for sensitive data</li>
              <li>Regular security audits and updates</li>
              <li>Secure server infrastructure</li>
              <li>Access controls and authentication protocols</li>
            </ul>
          </div>
          <p><strong>However, 100% data security cannot be guaranteed over the internet.</strong></p>
        </div>

        <div className="section">
          <h2>5. Children's Accounts</h2>
          <div className="highlight">
            <p>The application is used by medical students and healthcare professionals. For users under 18 years of age:</p>
            <ul>
              <li>We only collect data necessary for educational purposes</li>
              <li>Parents or guardians are provided access to monitor performance</li>
              <li>All data collection is under the supervision of the educational system</li>
              <li>Special protections apply to minors' personal information</li>
            </ul>
          </div>
        </div>

        <div className="section">
          <h2>6. User Rights</h2>
          <p>Users have the right to:</p>
          <ul>
            <li>Access their personal data</li>
            <li>Request modification or deletion of data</li>
            <li>Stop using the application at any time</li>
            <li>Export their data in a portable format</li>
            <li>Object to data processing for specific purposes</li>
            <li>Withdraw consent for data collection</li>
            <li>Lodge a complaint with relevant authorities</li>
          </ul>
          <p>To make such requests, please contact us via email.</p>
        </div>

        <div className="section">
          <h2>7. Data Retention</h2>
          <p>We retain user data for as long as:</p>
          <ul>
            <li>Your account is active</li>
            <li>Needed to provide our services</li>
            <li>Required by law or for legitimate business purposes</li>
          </ul>
          <p>Upon account deletion, personal data will be removed within 30 days, except where retention is required by law.</p>
        </div>

        <div className="section">
          <h2>8. Cookies and Tracking</h2>
          <p>Uro Crash may use cookies and similar technologies to:</p>
          <ul>
            <li>Remember user preferences and settings</li>
            <li>Analyze usage patterns and improve functionality</li>
            <li>Provide personalized content</li>
            <li>Ensure security and prevent fraud</li>
          </ul>
          <p>Users can manage cookie preferences through their browser settings.</p>
        </div>

        <div className="section">
          <h2>9. Third-Party Services</h2>
          <p>Our application may integrate with third-party services for:</p>
          <ul>
            <li>Payment processing</li>
            <li>Analytics and performance monitoring</li>
            <li>Cloud storage and hosting</li>
            <li>Communication services</li>
          </ul>
          <p>These services have their own privacy policies, and we encourage users to review them.</p>
        </div>

        <div className="section">
          <h2>10. Privacy Policy Changes</h2>
          <p>We may update the privacy policy from time to time to reflect:</p>
          <ul>
            <li>Changes in our practices</li>
            <li>Legal or regulatory requirements</li>
            <li>New features or services</li>
          </ul>
          <p>Users will be notified of any significant changes through the application or via email. Continued use of the service after changes indicates acceptance of the updated policy.</p>
        </div>

        <div className="section">
          <h2>11. Contact Us</h2>
          <p>If you have any questions about the privacy policy or wish to exercise your rights, you can contact us via:</p>
          <div className="highlight">
            <p><strong>📧 Email:</strong></p>
            <p><a href="mailto:educraft200000@gmail.com" className="email">educraft200000@gmail.com</a></p>
            <p><strong>📱 Support:</strong></p>
            <p>Available Monday - Friday, 9:00 AM - 6:00 PM</p>
          </div>
          <p>We will respond to all inquiries within 48 hours.</p>
        </div>

        <div className="footer">
          <p>&copy; 2026 Uro Crash. All rights reserved.</p>
          <p>Empowering Medical Education Excellence</p>
        </div>
      </div>
    </div>  
  );
}

export default App;
